// AIQuestionEditor.js - Add test results display at the bottom of each question
import React, { useState, useEffect } from 'react';
import { Row, Col, Input, Select, Checkbox, Button, Space, Form, Typography } from 'antd';
import {
    PlusOutlined,
    CloseOutlined,
    CheckOutlined,
    CloseCircleOutlined,
    CaretDownOutlined,
    CaretUpOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import EJSTemplateEditor from '../expressionEditor/EJSTemplateEditor';
import EnumValuesEditor from './EnumValuesEditor';
import { patternPresets, getPatternOptions } from './PatternPresets';

const { Option } = Select;
const { Text } = Typography;

const AIQuestionEditor = ({
                              fieldNameDisabled = false,
                              showAnswerType = true,
                              defaultType = 'string',
                              namePrefix, // Form.List name prefix
                              onRemove, // Callback to remove this question
                              onAddQuestion, // Callback to add a new question
                              showAddButton = false, // Whether to show the add button
                              showRemoveButton = true, // Whether to show the remove button
                              testResult = null, // Result of the test
                              testError = null, // Error from the test
                              testInProgress = false, // Whether a test is in progress
                              rootPath = [], // The root path of the form, used to detect duplication
                          }) => {
    // Log the namePrefix to help with debugging
    console.log('AIQuestionEditor rendering with namePrefix:', namePrefix);
    // Get form instance
    const form = Form.useFormInstance();

    // Local state for UI controls
    const [settingsExpanded, setSettingsExpanded] = useState(false);
    const [localAllowedValuesType, setLocalAllowedValuesType] = useState('any');
    const [localFieldType, setLocalFieldType] = useState(defaultType);
    const [localPattern, setLocalPattern] = useState('');
    const [selectedPatternPreset, setSelectedPatternPreset] = useState(undefined);

    // Initialize and sync form values with local state
    useEffect(() => {
        // Check if we have data duplication between root level and questions array
        const isRootLevelEditor = namePrefix.length === 1 && namePrefix[0] === '0';
        const questionsPath = ['questions', '0']; // Common path for questions array first item

        // Normal initialization
        const formAllowedValuesType = form.getFieldValue(['questions',...namePrefix, 'allowedValuesType']);
        const formFieldType = form.getFieldValue(['questions',...namePrefix, 'type']);
        const formPattern = form.getFieldValue(['questions',...namePrefix, 'pattern']);
        const formPatternPreset = form.getFieldValue(['questions',...namePrefix, 'patternPreset']);
        const formQuestion = form.getFieldValue(['questions',...namePrefix, 'question']);

        // Set default values if not already set in the form
        if (!formAllowedValuesType) {
            form.setFieldValue(['questions',...namePrefix, 'allowedValuesType'], 'any');
            setLocalAllowedValuesType('any');
        } else {
            setLocalAllowedValuesType(formAllowedValuesType);
        }

        if (!formQuestion) {
            form.setFieldValue(['questions',...namePrefix, 'question'], 'What is the customer\'s primary concern?');
        }

        if (!formFieldType) {
            form.setFieldValue(['questions',...namePrefix, 'type'], defaultType);
            setLocalFieldType(defaultType);
        } else {
            setLocalFieldType(formFieldType);
        }

        if (formPattern) {
            setLocalPattern(formPattern);
        }

        if (formPatternPreset) {
            setSelectedPatternPreset(formPatternPreset);
        }
    }, [form, namePrefix, defaultType]);

    const handleAllowedValuesTypeChange = (value) => {
        // Update local state immediately for responsive UI
        setLocalAllowedValuesType(value);

        // Update form value
        form.setFieldValue(['questions',...namePrefix, 'allowedValuesType'], value);

        // Handle different allowed values types
        if (value === 'enum') {
            const currentValues = form.getFieldValue(['questions',...namePrefix, 'allowedValues']);
            if (!currentValues || currentValues.length === 0) {
                form.setFieldValue(['questions',...namePrefix, 'allowedValues'], []);
            }
        }

        if (value !== 'enum') {
            form.setFieldValue(['questions',...namePrefix, 'allowedValues'], []);
        }

        if (value !== 'pattern') {
            form.setFieldValue(['questions',...namePrefix, 'pattern'], '');
            form.setFieldValue(['questions',...namePrefix, 'patternPreset'], undefined);
            setLocalPattern('');
            setSelectedPatternPreset(undefined);
        }
    };

    // Handle field type changes
    const handleFieldTypeChange = (value) => {
        setLocalFieldType(value);
        form.setFieldValue(['questions',...namePrefix, 'type'], value);
    };

    // Set pattern from preset
    // Set pattern from preset
    const handlePatternPresetChange = (presetKey) => {
        // Update the selected preset in local state
        setSelectedPatternPreset(presetKey);

        // Save the preset key to the form
        form.setFieldValue(['questions',...namePrefix, 'patternPreset'], presetKey);

        if (presetKey && patternPresets[presetKey]) {
            const newPattern = patternPresets[presetKey];
            // Update local pattern state
            setLocalPattern(newPattern);

            // Update form value with the correct path
            form.setFieldValue(['questions',...namePrefix, 'pattern'], newPattern);

            // Only attempt to clear parent pattern if we're in the questions namespace
            // This prevents accidentally clearing root form values when namePrefix is just [0]
            if (namePrefix.length >= 2 && namePrefix[0] === 'questions') {
                const parentPrefix = namePrefix.slice(0, -1);
                const currentParentPattern = form.getFieldValue(['questions',...parentPrefix, 'pattern']);
                if (currentParentPattern) {
                    form.setFieldValue(['questions',...parentPrefix, 'pattern'], undefined);
                }
            }

            console.log("Pattern preset change = " + newPattern);
        } else {
            // If no preset selected (cleared), keep the existing pattern
            form.setFieldValue(['questions',...namePrefix, 'patternPreset'], undefined);
        }
    };

    // Handle pattern input change
    const handlePatternChange = (e) => {
        const value = e.target.value;
        setLocalPattern(value);
        form.setFieldValue(['questions',...namePrefix, 'pattern'], value);

        // If the pattern was manually changed, clear the preset selection
        // since we're no longer using a preset pattern
        if (selectedPatternPreset) {
            setSelectedPatternPreset(undefined);
            form.setFieldValue(['questions',...namePrefix, 'patternPreset'], undefined);
        }

        console.log("Pattern change = " + value);
        const allValues = form.getFieldsValue(true); // Pass `true` to include unrendered fields
        console.log('All form values:', allValues);
    };

    // Format test result for display
    const formatTestResult = (result) => {
        if (result === null || result === undefined) return "No result";

        if (typeof result === 'object') {
            try {
                return JSON.stringify(result);
            } catch (e) {
                return "Complex result";
            }
        }

        return String(result);
    };

    // Generate testing result display
    const renderTestResult = () => {
        if (testError) {
            return (
                <div style={{ marginTop: 8 }}>
                    <Space align="center">
                        <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                        <Text type="danger">{testError}</Text>
                    </Space>
                </div>
            );
        }

        if (testResult !== null && testResult !== undefined) {
            const displayValue = formatTestResult(testResult);

            return (
                <div className="question-test-result" style={{
                    marginTop: 8,
                    padding: '4px 8px',
                    textAlign: 'left',
                    color: '#52c41a',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <Text style={{
                        color: '#52c41a',
                        wordBreak: 'break-word',
                        flex: 1
                    }}>
                        {displayValue}
                    </Text>
                </div>
            );
        }

        return null;
    };

    return (
        <div className="ai-question-editor">
            <Row gutter={8} align="middle" style={{ marginBottom: 8 }}>
                {/* Field Name */}
                <Col span={6}>
                    <Form.Item
                        name={[...namePrefix, 'fieldName']}
                        rules={[{ required: !fieldNameDisabled, message: 'Field name required' }]}
                        style={{ marginBottom: 0 }}
                    >
                        <Input placeholder="Field name" disabled={fieldNameDisabled} />
                    </Form.Item>
                </Col>

                {/* Question */}
                <Col span={16}>
                    <Form.Item
                        name={[...namePrefix, 'question']}
                        rules={[{ required: true, message: 'Question required' }]}
                        style={{ marginBottom: 0 }}
                    >
                        <EJSTemplateEditor
                            multiline={false}
                            height={36}
                            placeholder="What is the customer's primary concern?"
                            evaluateOnChange={true}
                            showModeToggle={false}
                            ultraCompact={true}
                        />
                    </Form.Item>
                </Col>

                {/* Action buttons */}
                <Col
                    span={2}
                    style={{
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        height: '36px',
                        justifyContent: 'center',
                        gap: '8px',
                    }}
                >
                    {/* Settings button */}
                    <Button
                        type="text"
                        icon={settingsExpanded ? <CaretUpOutlined /> : <CaretDownOutlined />}
                        onClick={() => setSettingsExpanded(!settingsExpanded)}
                        style={{ color: '#1890ff', padding: 0 }}
                    />

                    {/* Remove button */}
                    {showRemoveButton && (
                        <Button
                            type="text"
                            icon={<CloseOutlined />}
                            onClick={onRemove}
                            style={{ color: '#ff4d4f', padding: 0 }}
                        />
                    )}

                    {/* Add button - only shown on last row */}
                    {showAddButton && (
                        <Button
                            type="text"
                            icon={<PlusOutlined />}
                            onClick={onAddQuestion}
                            style={{ color: '#1890ff', padding: 0 }}
                        />
                    )}
                </Col>
            </Row>

            {/* Settings - Expands in place */}
            {settingsExpanded && (
                <div style={{
                    background: '#f9f9f9',
                    padding: '12px',
                    borderRadius: '4px',
                    marginBottom: '12px',
                    marginLeft: '6px'
                }}>
                    {showAnswerType && (
                        <Row gutter={16}>
                            <Col span={14}>
                                <Form.Item
                                    label="Answer Type"
                                    style={{ marginBottom: 12 }}
                                >
                                    <Select
                                        value={localFieldType}
                                        onChange={handleFieldTypeChange}
                                    >
                                        <Option value="string">Text</Option>
                                        <Option value="number">Number</Option>
                                        <Option value="boolean">Yes/No</Option>
                                        <Option value="decimal">Decimal</Option>
                                    </Select>
                                </Form.Item>
                                <Form.Item
                                    name={[...namePrefix, 'type']}
                                    initialValue={defaultType}
                                    hidden
                                >
                                    <Input />
                                </Form.Item>
                            </Col>
                            <Col span={10}>
                                <div style={{ display: 'flex', height: '100%', alignItems: 'center', paddingTop: '14px' }}>
                                    <Form.Item
                                        name={[...namePrefix, 'allowMultiple']}
                                        valuePropName="checked"
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Checkbox>Allow Multiple</Checkbox>
                                    </Form.Item>
                                </div>
                            </Col>
                        </Row>
                    )}

                    <Form.Item
                        label="Allowed Values"
                        style={{ marginBottom: 12 }}
                        name={[...namePrefix, 'allowedValuesType']}
                        initialValue="any"
                    >
                        <Select
                            value={localAllowedValuesType}
                            onChange={handleAllowedValuesTypeChange}
                            style={{ width: '100%' }}
                        >
                            <Option value="any">Any value</Option>
                            <Option value="enum">From list</Option>
                            <Option value="pattern">Pattern</Option>
                        </Select>
                    </Form.Item>

                    {/* Conditional rendering based on localAllowedValuesType */}
                    {localAllowedValuesType === 'enum' && (
                        <Form.Item name={[...namePrefix, 'allowedValues']} noStyle>
                            <EnumValuesEditor fieldType={localFieldType} />
                        </Form.Item>
                    )}
                    {localAllowedValuesType === 'pattern' && (
                        <div>
                            <Row gutter={8} align="middle">
                                <Col span={16}>
                                    <Form.Item
                                        name={[...namePrefix, 'pattern']}
                                        label="Pattern"
                                        style={{ marginBottom: 12 }}
                                    >
                                        <Input
                                            placeholder="Regular expression pattern"
                                            onChange={handlePatternChange}
                                            value={localPattern}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        label="Presets"
                                        style={{ marginBottom: 12 }}
                                    >
                                        <Select
                                            placeholder="Select preset"
                                            onChange={handlePatternPresetChange}
                                            style={{ width: '100%' }}
                                            allowClear
                                            value={selectedPatternPreset}
                                        >
                                            {getPatternOptions().map(option => (
                                                <Option key={option.value} value={option.value}>
                                                    {option.label}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                    )}
                </div>
            )}

            {/* Test results */}
            {renderTestResult()}
        </div>
    );
};

export default AIQuestionEditor;
